import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const CustomPrompt = ({ message, when, beforeReload, onProceed }: {
	message: string
	when: boolean
	beforeReload?: () => void
	onProceed?: () => void
}) => {
	const navigate = useNavigate()
	const location = useLocation()
	const promptShown = useRef(false)
	
	useEffect(() => {
		const handleBeforeUnload = (event: any) => {
			beforeReload && beforeReload()
			if (when) {
				event.preventDefault()
				event.returnValue = ''
			}
		}

		window.addEventListener('beforeunload', handleBeforeUnload)

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload)
		}
	}, [beforeReload, when])

	useEffect(() => {
		const handlePopState = () => {
			const confirmLeave = window.confirm(message)
			if (when && !promptShown.current) {
				beforeReload && beforeReload()
				if (confirmLeave) {
					promptShown.current = false // Reset for future prompts
					onProceed && onProceed()
				} else {
					// Prevent navigation by going back to the current path
					promptShown.current = true // Set to prevent further prompts
					navigate(location.pathname)
				}
			}
		}

		// Override the pushState method
		const originalPushState = window.history.pushState

		window.history.pushState = function (...args) {
			beforeReload && beforeReload()
			if (when && !promptShown.current) {
				const confirmLeave = window.confirm(message)
				if (!confirmLeave) {
					// Prevent navigation and return to the previous location
					promptShown.current = true // Set to prevent further prompts
					navigate(location.pathname)
					return // Prevent the original pushState from being called
				}
				onProceed && onProceed()
			}
			originalPushState.apply(this, args) // Call the original pushState
			promptShown.current = false // Reset for future prompts
		}

		window.addEventListener('popstate', handlePopState)

		return () => {
			window.removeEventListener('popstate', handlePopState)
			window.history.pushState = originalPushState // Restore original pushState
		}
	}, [when, message, navigate, location.pathname, beforeReload, onProceed])

	return null // This component doesn't render anything
}

export default CustomPrompt
